'use client';

import { PageBlocks } from 'src/components/PageBlocks';
import { Anchor } from 'src/components/ProductDetailed/AnchorMenu';
import { consoleRedirectUrl, consoleUrlReg } from 'src/global';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useFormatConsoleUrl } from 'src/hooks/useFormatConsoleUrl';
import { useIntersectionObserveForms } from 'src/hooks/useIntersectionObserverForms';
import { Layout } from 'src/layout';
import { Offer } from 'src/types/backendContent/Offers';
import { Variant } from 'src/uikit/Button/constants';
import { Form, FormForEnum } from 'src/uikit/Form';
import { ButtonProps, HeroInnerPage } from 'src/uikit/HeroInnerPage';
import { scrollToElementWithId } from 'src/utils/scrollToElementWithId';

export type OfferPageProps = {
  offer: Offer;
  anchors: Anchor[];
};

export function OfferPage(props: OfferPageProps) {
  const { offer, anchors } = props;

  const { content, form } = offer;

  const { formRef, setAnchorId } = useIntersectionObserveForms();

  const { customAnalytics } = useAnalytics();

  const logInUrl = useFormatConsoleUrl(
    consoleRedirectUrl,
    'body',
    'Вход в кабинет',
  );

  const rkoRegisterUrl = useFormatConsoleUrl(
    consoleUrlReg,
    'body',
    'Регистрация по СберБизнес ID',
  );

  const freeTierRegisterUrl = useFormatConsoleUrl(
    consoleUrlReg,
    'body',
    'Подключить',
  );

  const developersUrl = useFormatConsoleUrl(
    consoleUrlReg,
    'body',
    'попробовать бесплатно',
  );

  const studentsUrl = useFormatConsoleUrl(
    consoleUrlReg,
    'body',
    'Подключить облако',
  );

  const getPrimaryButtonRegisterUrl = () => {
    switch (offer.slug) {
      case 'rko':
        return `${rkoRegisterUrl}&registration_type=sbi`;
      case 'free-tier':
        return freeTierRegisterUrl;
      case 'developers':
        return developersUrl;
      case 'students':
        return studentsUrl;
      default:
        return undefined;
    }
  };

  const handleScrollToForm = () => {
    scrollToElementWithId('form');
    setAnchorId('');
  };

  const getPrimaryButtonClickHandler = () => {
    switch (offer.slug) {
      case 'rko':
        return undefined;
      case 'free-tier':
        return () => {
          //TODO: удалить customAnalytics после тестов. вернуть undefined
          customAnalytics('AB_free_tier_click_connect', ['']);
        };
      case 'developers':
        return undefined;
      case 'students':
        return undefined;
      default:
        return handleScrollToForm;
    }
  };

  const primaryButton: ButtonProps = {
    title: offer.buttonTitle || 'Связаться с нами',
    analyticsOptions: {
      action: 'click',
      clickZone: 'body',
      clickElement: 'button',
      clickContent: offer.buttonTitle || 'Связаться с нами',
      uniqueId: `offers-${offer.slug}-hero-primary-button`,
      transitionType: 'inside-link',
      clickZoneCategory: 'site-body',
    },
    href: getPrimaryButtonRegisterUrl(),
    onClick: getPrimaryButtonClickHandler(),
  };

  const secondaryButton: ButtonProps | undefined =
    offer.slug === 'rko'
      ? {
          title: 'Вход в кабинет',
          href: logInUrl,
          variant: Variant.Tertiary,
          analyticsOptions: {
            action: logInUrl,
            clickZone: 'body',
            clickElement: 'button',
            clickContent: 'Вход в кабинет',
            uniqueId: 'hero-button-rko-login',
            transitionType: 'inside-link',
            clickZoneCategory: 'site-body',
            transitionUrl: logInUrl,
            transitionTypeMetrics: 'internal',
          },
        }
      : undefined;

  const buttons: ButtonProps[] = [primaryButton, secondaryButton].filter(
    (b): b is ButtonProps => Boolean(b),
  );

  return (
    <Layout>
      <HeroInnerPage
        tags={offer.tags}
        title={offer.title}
        description={offer.description}
        heroImage={offer.icon3d}
        anchorMenu={{ anchors, slug: offer.slug }}
        breadcrumbs={{ links: content.breadcrumbs }}
        buttons={buttons}
      />
      <PageBlocks blocks={content.blocks} />
      {form && (
        <Form
          id="form"
          formFor={FormForEnum.Registry}
          ref={formRef}
          inputPath="v2/order"
          title={form?.title}
          subtitle={form?.description}
          buttonTitle={form?.buttonTitle}
          fields={form?.fields}
          productSlug={offer.slug}
          marketingEventIDs={
            offer.marketingEventID ? [offer.marketingEventID] : []
          }
          successNotice={{ title: form?.successText }}
          errorNotice={{ title: form?.failedText }}
          analyticsFieldFill={{ formType: 'body' }}
          formVariant="inner"
          allFormsSentFormType="sale"
          successfulSubmissonFormAnalytics={{
            formname: 'lead-offers',
          }}
        />
      )}
    </Layout>
  );
}
